// client/src/utils/timezoneUtils.js
/**
 * =================================================================
 * Timezone Utilities for Multi-Tenant Application
 * =================================================================
 * Handles timezone conversions between:
 * - User's local browser timezone
 * - Tenant's configured timezone
 * - UTC for database storage
 * =================================================================
 */

/**
 * Timezones covering the regions whose languages the app supports
 * (see SUPPORTED_LANGUAGES in src/i18n/config.js), plus the major US
 * zones. Ordered roughly west-to-east so the dropdown reads naturally.
 */
export const TIMEZONES = {
  // North America (en-US, es-MX)
  'Pacific/Honolulu':       'Hawaii Time (HST)',
  'America/Anchorage':      'Alaska Time (AKT)',
  'America/Los_Angeles':    'Pacific Time (PT)',
  'America/Phoenix':        'Arizona Time (MST)',
  'America/Denver':         'Mountain Time (MT)',
  'America/Chicago':        'Central Time (CT)',
  'America/Mexico_City':    'Mexico City (CST)',
  'America/New_York':       'Eastern Time (ET)',
  'America/Toronto':        'Toronto (ET)',
  // South America (pt-BR)
  'America/Sao_Paulo':      'São Paulo (BRT)',
  // Europe (en-GB, es-ES, fr-FR, de-DE, it-IT)
  'Europe/London':          'London (GMT/BST)',
  'Europe/Madrid':          'Madrid (CET)',
  'Europe/Paris':           'Paris (CET)',
  'Europe/Berlin':          'Berlin (CET)',
  'Europe/Rome':            'Rome (CET)',
  // Africa (sw-KE, ha-NG, am-ET)
  'Africa/Lagos':           'Lagos (WAT)',
  'Africa/Addis_Ababa':     'Addis Ababa (EAT)',
  'Africa/Nairobi':         'Nairobi (EAT)',
  // Middle East (ar-SA, he-IL)
  'Asia/Jerusalem':         'Jerusalem (IST)',
  'Asia/Riyadh':            'Riyadh (AST)',
  // Asia (zh-CN, zh-TW, ko-KR, ja-JP)
  'Asia/Shanghai':          'Shanghai (CST)',
  'Asia/Taipei':            'Taipei (CST)',
  'Asia/Seoul':             'Seoul (KST)',
  'Asia/Tokyo':             'Tokyo (JST)',
  // Reference
  'UTC':                    'UTC',
};

/**
 * Get list of timezone options for dropdown
 * @returns {Array} Array of {value, label} objects
 */
export const getTimezoneOptions = () => {
  return Object.entries(TIMEZONES).map(([value, label]) => ({
    value,
    label: `${label} (${value})`,
  }));
};

/**
 * Convert a date/time from one timezone to another
 * @param {Date|string} dateTime - The datetime to convert
 * @param {string} fromTZ - Source timezone (IANA format)
 * @param {string} toTZ - Target timezone (IANA format)
 * @returns {Date} Converted date
 */
export const convertTimezone = (dateTime, fromTZ, toTZ) => {
  const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
  
  // Convert to string in source timezone
  const sourceString = date.toLocaleString('en-US', { timeZone: fromTZ });
  
  // Parse as if in target timezone
  const targetDate = new Date(sourceString);
  
  return targetDate;
};

/**
 * Format datetime in tenant's timezone
 * @param {Date|string} dateTime - The datetime
 * @param {string} tenantTZ - Tenant's timezone
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted datetime string
 */
export const formatInTenantTZ = (dateTime, tenantTZ, options = {}) => {
  const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
  
  const defaultOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: tenantTZ,
    hour12: true,
  };
  
  return date.toLocaleString('en-US', { ...defaultOptions, ...options });
};

/**
 * Get current time in tenant's timezone
 * @param {string} tenantTZ - Tenant's timezone
 * @returns {Date} Current date in tenant timezone
 */
export const getNowInTenantTZ = (tenantTZ) => {
  const now = new Date();
  const tzString = now.toLocaleString('en-US', { timeZone: tenantTZ });
  return new Date(tzString);
};

/**
 * Convert local date/time inputs to UTC for database storage
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @param {string} timeStr - Time in HH:MM format
 * @param {string} tenantTZ - Tenant's timezone
 * @returns {string} UTC datetime in MySQL format (YYYY-MM-DD HH:MM:SS)
 */
export const localToUTC = (dateStr, timeStr, tenantTZ) => {
  if (!dateStr || !timeStr) return null;

  const [year, month, day] = dateStr.split('-').map(Number);
  const [hours, minutes] = timeStr.split(':').map(Number);

  // Treat the input digits as if they were UTC, then compute the real offset.
  const tempDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));

  // Use formatToParts with hourCycle:'h23' to get structured values (0-23 hours).
  // This avoids the fragile toLocaleString→new Date() parse path, where Chrome/ICU
  // on Android can return PM hours without AM/PM notation when hour12:false is used,
  // causing new Date() to misparse them as AM and reversing the UTC offset sign.
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tenantTZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

  const parts = dtf.formatToParts(tempDate);
  const getPart = (type) => Number(parts.find(p => p.type === type)?.value ?? 0);

  const tY   = getPart('year');
  const tMo  = getPart('month');
  const tD   = getPart('day');
  const tH   = getPart('hour') % 24; // guard: some ICU builds return 24 for midnight (h24 cycle)
  const tMin = getPart('minute');

  // Offset = (input treated as UTC) − (tenant clock digits treated as UTC)
  // e.g. for UTC-6: tempDate=10:05Z, tenant shows 04:05 → offset = +6h → correctUTC = 16:05Z
  const tenantAsUtcMs = Date.UTC(tY, tMo - 1, tD, tH, tMin, 0);
  const offsetMs = tempDate.getTime() - tenantAsUtcMs;
  const correctUTC = new Date(tempDate.getTime() + offsetMs);

  const Y  = correctUTC.getUTCFullYear();
  const Mo = String(correctUTC.getUTCMonth() + 1).padStart(2, '0');
  const D  = String(correctUTC.getUTCDate()).padStart(2, '0');
  const H  = String(correctUTC.getUTCHours()).padStart(2, '0');
  const Mi = String(correctUTC.getUTCMinutes()).padStart(2, '0');
  const S  = String(correctUTC.getUTCSeconds()).padStart(2, '0');

  return `${Y}-${Mo}-${D} ${H}:${Mi}:${S}`;
};

/**
 * Convert UTC datetime from database to tenant timezone
 * @param {string} utcDateTimeStr - UTC datetime string from database
 * @param {string} tenantTZ - Tenant's timezone
 * @returns {object} Object with {date, time} in tenant timezone
 */
export const utcToLocal = (utcDateTimeStr, tenantTZ) => {
  if (!utcDateTimeStr) return { date: '', time: '09:00' };
  
  // Parse UTC datetime
  const utcDate = new Date(utcDateTimeStr + (utcDateTimeStr.includes('Z') ? '' : 'Z'));
  
  // Convert to tenant timezone
  const tzString = utcDate.toLocaleString('en-US', {
    timeZone: tenantTZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  
  // Parse the formatted string: "MM/DD/YYYY, HH:MM"
  const [datePart, timePart] = tzString.split(', ');
  const [month, day, year] = datePart.split('/');
  const date = `${year}-${month}-${day}`;
  
  // Round to nearest 5 minutes
  const [hours, minutes] = timePart.split(':').map(Number);
  const roundedMinutes = Math.round(minutes / 5) * 5;
  const finalMinutes = roundedMinutes === 60 ? 0 : roundedMinutes;
  const finalHours = roundedMinutes === 60 ? (hours + 1) % 24 : hours;
  const time = `${String(finalHours).padStart(2, '0')}:${String(finalMinutes).padStart(2, '0')}`;
  
  return { date, time };
};

/**
 * Get timezone offset display (e.g., "UTC-5" or "UTC-8")
 * @param {string} tenantTZ - Tenant's timezone
 * @param {Date} date - Optional date to check (for DST)
 * @returns {string} Offset display string
 */
export const getTimezoneOffset = (tenantTZ, date = new Date()) => {
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone: tenantTZ }));
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  
  const offsetMinutes = (tzDate - utcDate) / (1000 * 60);
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
  const offsetMins = Math.abs(offsetMinutes) % 60;
  
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const minsStr = offsetMins > 0 ? `:${String(offsetMins).padStart(2, '0')}` : '';
  
  return `UTC${sign}${offsetHours}${minsStr}`;
};

/**
 * Check if tenant is currently in daylight saving time
 * @param {string} tenantTZ - Tenant's timezone
 * @returns {boolean} True if DST is active
 */
export const isDST = (tenantTZ) => {
  const now = new Date();
  const january = new Date(now.getFullYear(), 0, 1);
  const july = new Date(now.getFullYear(), 6, 1);
  
  const janOffset = getTimezoneOffset(tenantTZ, january);
  const julOffset = getTimezoneOffset(tenantTZ, july);
  
  return janOffset !== julOffset;
};

/**
 * Get next valid 5-minute time slot in tenant timezone
 * @param {string} tenantTZ - Tenant's timezone
 * @param {number} minutesAhead - Minutes to add (default 15)
 * @returns {object} Object with {date, time}
 */
export const getNextSlotInTenantTZ = (tenantTZ, minutesAhead = 15) => {
  const now = getNowInTenantTZ(tenantTZ);
  now.setMinutes(now.getMinutes() + minutesAhead);
  
  // Round up to next 5-minute increment
  const minutes = now.getMinutes();
  const roundedMinutes = Math.ceil(minutes / 5) * 5;
  
  if (roundedMinutes === 60) {
    now.setHours(now.getHours() + 1);
    now.setMinutes(0);
  } else {
    now.setMinutes(roundedMinutes);
  }
  
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const mins = String(now.getMinutes()).padStart(2, '0');
  
  return {
    date: `${year}-${month}-${day}`,
    time: `${hours}:${mins}`,
  };
};

/**
 * Validate that a datetime is in the future (in tenant timezone)
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @param {string} timeStr - Time in HH:MM format
 * @param {string} tenantTZ - Tenant's timezone
 * @returns {boolean} True if datetime is in the future
 */
export const isFuture = (dateStr, timeStr, tenantTZ) => {
  if (!dateStr || !timeStr) return false;
  
  const inputDate = new Date(`${dateStr}T${timeStr}:00`);
  const nowInTZ = getNowInTenantTZ(tenantTZ);
  
  return inputDate > nowInTZ;
};

/**
 * Format timezone-aware datetime for display
 * @param {string} dateStr - Date string
 * @param {string} timeStr - Time string
 * @param {string} tenantTZ - Tenant timezone
 * @returns {string} Formatted string with timezone
 */
export const formatDateTimeWithTZ = (dateStr, timeStr, tenantTZ) => {
  if (!dateStr || !timeStr) return '';
  
  const date = new Date(`${dateStr}T${timeStr}:00`);
  const formatted = date.toLocaleString('en-US', {
    timeZone: tenantTZ,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  
  const offset = getTimezoneOffset(tenantTZ);
  const tzName = TIMEZONES[tenantTZ] || tenantTZ;
  
  return `${formatted} (${tzName} ${offset})`;
};